import { useEffect, useRef } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { Id } from '../../convex/_generated/dataModel'
import { isElectron } from '@/lib/electron'

type ElectronAPI = {
  executeLocalTool: (toolName: string, input: unknown) => Promise<unknown>
  scanDirectory: (directory: string) => Promise<unknown>
  getFileMetadata: (filePath: string) => Promise<unknown>
}

function getElectronAPI(): ElectronAPI | null {
  if (!isElectron()) return null
  return (window as any).electronAPI as ElectronAPI
}

export function useLocalToolBridge(workflowId: Id<'workflows'> | null) {
  const pending = useQuery(
    api.workflows.getPendingLocalToolCalls,
    workflowId ? { workflowId } : 'skip'
  )
  const submitResults = useMutation(api.workflows.submitLocalToolResults)
  const setDesktopMode = useMutation(api.workflows.setDesktopMode)
  const executingRef = useRef(false)

  useEffect(() => {
    if (workflowId && isElectron()) {
      setDesktopMode({ workflowId }).catch(() => {})
    }
  }, [workflowId, setDesktopMode])

  useEffect(() => {
    if (!pending?.calls?.length || !workflowId || executingRef.current) return

    const electronAPI = getElectronAPI()
    if (!electronAPI) {
      submitResults({
        workflowId,
        results: pending.calls.map(c => ({
          toolCallId: c.toolCallId,
          result: { success: false, error: 'Desktop app required for local file operations' },
        })),
      }).catch(err => console.error('[LocalToolBridge] Failed to submit non-electron results:', err))
      return
    }

    executingRef.current = true

    const execute = async () => {
      try {
        const results = await Promise.all(
          pending.calls.map(async (call) => {
            try {
              let result: unknown
              switch (call.toolName) {
                case 'scan_local_directory':
                  result = await electronAPI.scanDirectory(call.input.directory as string)
                  break
                case 'get_local_file_metadata':
                  result = await electronAPI.getFileMetadata(call.input.file_path as string)
                  break
                default:
                  result = await electronAPI.executeLocalTool(call.toolName, call.input)
              }
              return { toolCallId: call.toolCallId, result: { success: true, data: result } }
            } catch (err) {
              return {
                toolCallId: call.toolCallId,
                result: { success: false, error: err instanceof Error ? err.message : String(err) },
              }
            }
          })
        )

        await submitResults({ workflowId, results })
      } catch (err) {
        console.error('[LocalToolBridge] Failed to submit results:', err)
        try {
          await submitResults({
            workflowId,
            results: pending.calls.map(c => ({
              toolCallId: c.toolCallId,
              result: { success: false, error: `Bridge error: ${err instanceof Error ? err.message : String(err)}` },
            })),
          })
        } catch {
          console.error('[LocalToolBridge] Failed to submit fallback error results')
        }
      } finally {
        executingRef.current = false
      }
    }

    execute()
  }, [pending, workflowId, submitResults])
}
